import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import apiClient from '../api/client';
import { toast } from "sonner";
import { ArrowLeft, Play, Loader2, LogOut } from "lucide-react";
import { useNavigate } from 'react-router-dom';

export default function AdminCalibration() {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeScenario, setActiveScenario] = useState(null);

  const runScenario = async (scenario) => {
      setLoading(true);
      setResult(null);
      setActiveScenario(scenario);
      
      let config = {
        customer_type: 'privat',
        price_model: 'fastpris',
        u_cabinets: 0, v_cabinets: 0, h_cabinets: 0,
        island: 'ingen', worktop: 'laminat', panels: 'medel',
        floor_laid: true, demolition: false, disposal: false,
        electrical: 'ingen', plumbing: 'ingen', logistics: 'normal',
        painting_walls: 0, painting_ceiling: 0
      };

      if (scenario === 'standard') {
          config = { ...config, u_cabinets: 8, v_cabinets: 6, h_cabinets: 2, integrated_dishwasher: 1, oven_micro: 1, hob: 1, fan: 1 };
      } else if (scenario === 'large') {
          config = { ...config, u_cabinets: 12, v_cabinets: 8, h_cabinets: 4, island: 'stor', worktop: 'sten', panels: 'hog', electrical: 'medium', plumbing: 'flytt' };
      } else if (scenario === 'small') {
          config = { ...config, u_cabinets: 4, v_cabinets: 2, h_cabinets: 1 };
      }

      try {
          const res = await apiClient.post('/calculate', {
              config,
              contact: { name: "Test", email: "test@test.se", phone: "123" },
              photos: [], 
              dimensions: { wall_a: 3000, wall_b: 2000, ceiling_height: 2400 } // Simulate valid dims
          });
          setResult({ name: scenario, data: res.data });
      } catch (e) {
          toast.error("Error running scenario");
      } finally {
          setLoading(false);
      }
  };

  const logout = () => {
      localStorage.removeItem('admin_token');
      navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
                <Button variant="outline" onClick={() => navigate('/admin/dashboard')}><ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka</Button>
                <h1 className="text-3xl font-bold text-primary">Kalibrering & Test</h1>
            </div>
            <Button variant="ghost" onClick={logout} title="Logga ut"><LogOut className="h-4 w-4" /></Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
                size="lg" 
                variant="outline" 
                onClick={() => runScenario('small')} 
                className={`h-24 flex flex-col items-center justify-center transition-all ${activeScenario === 'small' ? 'ring-2 ring-secondary bg-blue-50 border-secondary' : 'hover:border-slate-400'}`}
            >
                <span className="font-bold text-lg">Litet Kök</span>
                <span className="text-xs text-muted-foreground">4U + 2V + 1H</span>
            </Button>
            <Button 
                size="lg" 
                variant="outline" 
                onClick={() => runScenario('standard')} 
                className={`h-24 flex flex-col items-center justify-center transition-all ${activeScenario === 'standard' ? 'ring-2 ring-secondary bg-blue-50 border-secondary' : 'hover:border-slate-400'}`}
            >
                <span className="font-bold text-lg">Standardkök</span>
                <span className="text-xs text-muted-foreground">8U + 6V + 2H + Vitvaror</span>
            </Button>
            <Button 
                size="lg" 
                variant="outline" 
                onClick={() => runScenario('large')} 
                className={`h-24 flex flex-col items-center justify-center transition-all ${activeScenario === 'large' ? 'ring-2 ring-secondary bg-blue-50 border-secondary' : 'hover:border-slate-400'}`}
            >
                <span className="font-bold text-lg">Stort Lyxkök</span>
                <span className="text-xs text-muted-foreground">Ö, Sten, El/VVS Flytt</span>
            </Button>
        </div>

        {loading && <div className="p-8 text-center"><Loader2 className="animate-spin h-8 w-8 mx-auto" /></div>}

        {result && (
            <Card className="animate-in fade-in slide-in-from-bottom-4 shadow-md border-primary/20">
                <CardHeader>
                    <CardTitle>Resultat: {result.name === 'small' ? 'Litet Kök' : result.name === 'standard' ? 'Standardkök' : 'Stort Lyxkök'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="p-4 bg-slate-100 rounded">
                            <div className="text-xs text-muted-foreground uppercase">Timmar (Med)</div>
                            <div className="text-3xl font-bold">{result.data.hours_median} h</div>
                        </div>
                        <div className="p-4 bg-slate-100 rounded">
                            <div className="text-xs text-muted-foreground uppercase">Pris (Rek)</div>
                            <div className="text-3xl font-bold text-secondary">{result.data.price_rec.toLocaleString()} kr</div>
                        </div>
                        <div className="p-4 bg-slate-100 rounded">
                            <div className="text-xs text-muted-foreground uppercase">Spann</div>
                            <div className="text-xl font-bold">±{Math.round(result.data.risk_factor * 100)}%</div>
                        </div>
                    </div>

                    <div>
                        <h3 className="font-bold mb-2">Specifikation</h3>
                        <div className="border rounded-md divide-y">
                            {result.data.breakdown.map((row, i) => (
                                <div key={i} className="flex justify-between p-2 text-sm hover:bg-slate-50">
                                    <span>{row.description} <span className="text-muted-foreground text-xs">({row.count} st)</span></span>
                                    <span className="font-mono">{row.total_med} h</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </CardContent>
            </Card>
        )}
      </div>
    </div>
  );
}
